import React, { useContext } from "react";
import { NavLink } from "react-router-dom";
import { useAdminAuth } from "../../contexts/AdminAuthContext";
import { AuthContext } from "../../Hook/AuthProvider";
import { PERMISSIONS } from "../../constants/adminPermissions";
import { ROUTES } from "../../constants/routes";
import { getInitials } from "../../Helpers/stringHelpers";
import {
  Layers,
  FileQuestion,
  Users,
  UserCheck,
  BarChart3,
  Settings,
  ClipboardList,
  AlertCircle,
  Bell,
  CreditCard,
  ShieldCheck,
  FileText
} from "lucide-react";

export default function AdminSidebar({ isCollapsed, onToggleCollapse }) {
  const { can, canAny, role, adminUser } = useAdminAuth();
  const { profileData } = useContext(AuthContext);

  // Determine user display name & initials matching user screenshot
  const firstName = profileData?.first_name || adminUser?.name || "sudhansu";
  const lastName = profileData?.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim();
  const initials = fullName ? getInitials(fullName) : "SS";

  // Navigation definition according to Section 2 of ADMIN_MGMT.MD
  const navSections = [
    {
      title: "Tests",
      permissionCheck: () => canAny([PERMISSIONS.TESTS_READ, PERMISSIONS.TESTS_CREATE]),
      items: [
        {
          label: "Test Builder",
          route: ROUTES.ADMIN_TESTS_BUILDER,
          icon: ClipboardList,
          permission: PERMISSIONS.TESTS_CREATE,
        },
        {
          label: "Test Management",
          route: ROUTES.ADMIN_TESTS_MANAGEMENT,
          icon: Layers,
          permission: PERMISSIONS.TESTS_READ,
        },
      ],
    },
    {
      title: "Questions",
      permissionCheck: () => canAny([PERMISSIONS.QUESTIONS_READ, PERMISSIONS.QUESTIONS_CREATE]),
      items: [
        {
          label: "Question Bank",
          route: ROUTES.ADMIN_QUESTIONS_BANK,
          icon: FileQuestion,
          permission: PERMISSIONS.QUESTIONS_READ,
        },
        {
          label: "Question Reports",
          route: ROUTES.ADMIN_QUESTIONS_REPORTS,
          icon: AlertCircle,
          permission: PERMISSIONS.QUESTIONS_REVIEW,
        },
      ],
    },
    {
      title: "Users",
      permissionCheck: () => can(PERMISSIONS.USERS_READ),
      items: [
        {
          label: "User Management",
          route: ROUTES.ADMIN_USERS,
          icon: Users,
          permission: PERMISSIONS.USERS_READ,
        },
      ],
    },
    {
      title: "Mentors",
      permissionCheck: () => canAny([PERMISSIONS.MENTORS_READ, PERMISSIONS.NOTIFICATIONS_CREATE]),
      items: [
        {
          label: "User Assignment",
          route: ROUTES.ADMIN_MENTORS_ASSIGNMENT,
          icon: UserCheck,
          permission: PERMISSIONS.MENTORS_READ,
        },
        {
          label: "Notifications",
          route: ROUTES.ADMIN_MENTORS_NOTIFICATIONS,
          icon: Bell,
          permission: PERMISSIONS.NOTIFICATIONS_CREATE,
        },
        {
          label: "User Reports",
          route: ROUTES.ADMIN_MENTORS_REPORTS,
          icon: FileText,
          permission: PERMISSIONS.MENTORS_READ,
        },
      ],
    },
    {
      title: "Analytics",
      permissionCheck: () => can(PERMISSIONS.ANALYTICS_READ),
      items: [
        {
          label: "User Analytics",
          route: ROUTES.ADMIN_ANALYTICS_USERS,
          icon: BarChart3,
          permission: PERMISSIONS.ANALYTICS_READ,
        },
        {
          label: "Test Analytics",
          route: ROUTES.ADMIN_ANALYTICS_TESTS,
          icon: Layers,
          permission: PERMISSIONS.ANALYTICS_READ,
        },
        {
          label: "Payments / Subscriptions",
          route: ROUTES.ADMIN_ANALYTICS_PAYMENTS,
          icon: CreditCard,
          permission: PERMISSIONS.ANALYTICS_READ,
        },
      ],
    },
    {
      title: "System",
      permissionCheck: () => canAny([PERMISSIONS.ADMIN_MANAGE, PERMISSIONS.AUDIT_READ]),
      items: [
        {
          label: "Admin Users",
          route: ROUTES.ADMIN_SYSTEM_USERS,
          icon: ShieldCheck,
          permission: PERMISSIONS.ADMIN_MANAGE,
        },
        {
          label: "Roles & Permissions",
          route: ROUTES.ADMIN_SYSTEM_ROLES,
          icon: Settings,
          permission: PERMISSIONS.ADMIN_MANAGE,
        },
        {
          label: "Audit Logs",
          route: ROUTES.ADMIN_SYSTEM_AUDIT,
          icon: FileText,
          permission: PERMISSIONS.AUDIT_READ,
        },
      ],
    },
  ];

  return (
    <div
      className="sidebar-wrapper admin-sidebar-wrapper"
      data-collapsed={isCollapsed}
    >
      {/* Authentic floating circular toggle button on sidebar edge */}
      <button
        className="sidebar-floating-toggle"
        onClick={onToggleCollapse}
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {isCollapsed ? "\u203A" : "\u2039"}
      </button>

      {/* Sidebar shell with identical dimensions and styles as student app */}
      <div
        className={`sidebar sidebar-shell admin-sidebar-shell ${
          isCollapsed ? "Iside isvisible collapsed" : "Notiside expanded"
        }`}
        data-sidebar-state={isCollapsed ? "collapsed" : "expanded"}
      >
        {/* Navigation Section Links */}
        <div className="admin-sidebar-nav-container">
          {navSections.map((section) => {
            if (!section.permissionCheck()) return null;

            const authorizedItems = section.items.filter((item) => can(item.permission));
            if (authorizedItems.length === 0) return null;

            return (
              <div key={section.title} className="admin-nav-section-group">
                {!isCollapsed && (
                  <div className="admin-section-heading">{section.title}</div>
                )}
                {authorizedItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.route}
                      to={item.route}
                      className={({ isActive }) =>
                        `nav-link sidebar-menu-item admin-menu-item ${isActive ? "bg active-admin-link" : ""}`
                      }
                      title={isCollapsed ? item.label : undefined}
                    >
                      <div className="sidebar-menu-content admin-menu-content">
                        <Icon size={16} className="admin-icon" />
                        {!isCollapsed && (
                          <span className="admin-menu-label">{item.label}</span>
                        )}
                      </div>
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Sidebar Footer: User Profile ONLY (No Exit to Student App button) */}
        <div
          className="user-section admin-sidebar-user-section"
          title={`Signed in as ${fullName || firstName} (${role || "Super Admin"})`}
        >
          <div className="user-section-content">
            <div className="user-info">
              <div className="user-avatar sidebar-user-avatar">
                <div className="menubar-wrapper profile-icon">
                  <div className="user-logo user-logo-profile mobile-profile custom-profile-logo">
                    <span>{initials}</span>
                  </div>
                  <div className="proicon-open">
                    <img
                      src="/assets/images/pro-button-gradient-1.svg"
                      alt="Pro"
                      className="proicon-profileicon"
                    />
                  </div>
                </div>
              </div>
              {!isCollapsed && (
                <div className="user-details user-details-text">
                  <span className="user-name">{firstName}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
